/**
 * Dismissible install tip for iPhone / iPad / Android.
 * Hidden when already running as an installed PWA (standalone / fullscreen).
 */
import { useEffect, useMemo, useState } from 'react';
import { Download, Share, X } from 'lucide-react';

const DISMISS_KEY = 'vibe-hr-install-tip-dismissed';

function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return true;
  const mq = window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mq || iosStandalone;
}

function isAppleTouchDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const apple = useMemo(() => isAppleTouchDevice(), []);

  useEffect(() => {
    try {
      if (isStandaloneDisplay()) return;
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      const touchy = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 1024;
      if (!touchy) return;
      setVisible(true);
    } catch {
      // ignore
    }
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setVisible(false);
  };

  return (
    <div
      role="status"
      className="safe-px safe-pb border-b border-[var(--color-rule)] bg-[var(--color-surface)] text-[var(--color-ink)]"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3 py-3">
        <div className="mt-0.5 rounded-md border border-[var(--color-rule)] bg-[var(--color-paper-2)] p-2 text-[var(--color-ink)] shadow-[var(--shadow-brutal-sm)]">
          <Download className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold">Install Vibe Wireless HR</p>
          {apple ? (
            <p className="mt-0.5 font-medium text-[var(--color-muted)]">
              Tap <Share className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" aria-hidden /> Share, then{' '}
              <span className="font-bold text-[var(--color-ink)]">Add to Home Screen</span> for a full-screen app on this device.
            </p>
          ) : (
            <p className="mt-0.5 font-medium text-[var(--color-muted)]">
              Use your browser menu → <span className="font-bold text-[var(--color-ink)]">Install app</span> /{' '}
              <span className="font-bold text-[var(--color-ink)]">Add to Home screen</span> for quicker access with an offline-ready shell.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="app-icon-button"
          aria-label="Dismiss install tip"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
